import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import qs from 'qs';
import { HeaderFiltersType } from 'context/GlobalFiltersContext/initialState';
import { LkpChart } from '../Chart';
import { useBoolean, useDebounce } from 'hooks';
import { ChartSeriesType, ChartsParamsType } from '../types';
import { fetcher } from 'services';
import { ChartNumberOfOccurrencesRegisteredPerWeekType } from './types';
import { getErrorMessageByStatusCode, groupBy } from 'utils';
import { getLineChartOptions } from './options';
import { LkpChartTopFilter } from '../Filters';
//import { useParams } from 'react-router-dom';

export interface LkpChartNumberOfOccurrencesRegisteredPerWeekProps {
  headerFilters: HeaderFiltersType;
  inicio?: string;
  fim?: string;
  sistemas?: any[];
  externalData?: any;
  externalLoading?: boolean;
  disableFetch?: boolean;
  retryFetch?: () => void;
}

const HTTP_PATH = '/quantidade-ocorrencias-por-periodo';

export function LkpChartNumberOfOccurrencesRegisteredPerWeek({
  headerFilters,
  inicio,
  fim,
  sistemas,
  externalData,
  externalLoading = false,
  disableFetch = false,
  retryFetch,
}: LkpChartNumberOfOccurrencesRegisteredPerWeekProps): ReactElement {
  const { value: isLoading, setValue: setLoading } = useBoolean();
  const isLoadingFinal = externalLoading || isLoading;
  const { value: isError, setValue: setIsError } = useBoolean();
  const [errorMessage, setErrorMessage] = useState<string>();
  //const { dateFrom, dateTo } = useParams<{ dateFrom: string; dateTo: string }>();
  const [series, setSeries] = useState<Array<ChartSeriesType>>([]);
  const [periods, setPeriods] = useState<Array<string>>([]);
  const [params, setParams] = useState<ChartsParamsType>({
    Empresa: headerFilters?.companies?.id || 0,
    Sites: headerFilters?.units?.id,
    Inicio: inicio,
    Fim: fim,
    GrupoSite: headerFilters?.unitGroups?.id || [],
    SubGrupoSite: headerFilters?.unitSubgroups?.id || [],
    Cidade: headerFilters?.cities?.id || [],
    Estado: headerFilters?.states?.id || [],
    Sistemas: sistemas || [],
  });

  const debouncedStartValue = useDebounce<string>(params?.Inicio || '', 500);
  const debouncedEnvValue = useDebounce<string>(params?.Fim || '', 500);
  useEffect(() => {
    setParams(prev => ({
      ...prev,
      Empresa: headerFilters?.companies?.id || 0,
      Sites: headerFilters?.units?.id,
      GrupoSite: headerFilters?.unitGroups?.id || [],
      SubGrupoSite: headerFilters?.unitSubgroups?.id || [],
      Cidade: headerFilters?.cities?.id || [],
      Estado: headerFilters?.states?.id || [],
      Inicio: inicio,
      Fim: fim,
    }));
  }, [
    inicio,
    fim,
    sistemas,
    headerFilters?.companies?.id,
    headerFilters?.units?.id,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.cities?.id,
    headerFilters?.states?.id,
  ]);

  useEffect(() => {
    if (disableFetch && externalData) {
      const _series = externalData.map(({ quantidade }) => quantidade || 0);
      setSeries([{ name: 'Quantidade', data: _series }]);

      const _groupPeriods = groupBy<any>(externalData, d => d.periodo);
      setPeriods(_groupPeriods.map(({ name }) => name));
      return;
    }

    if (disableFetch) return;

    (async () => {
      await fetchChartData();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedStartValue,
    debouncedEnvValue,
    sistemas,
    headerFilters?.unitGroups?.id,
    headerFilters?.unitSubgroups?.id,
    headerFilters?.companies?.id,
    headerFilters?.states?.id,
    headerFilters?.cities?.id,
    disableFetch,
    externalData,
  ]);

  const fetchChartData = async () => {
    setLoading(true);

    const result = await fetcher
      .get<Array<ChartNumberOfOccurrencesRegisteredPerWeekType>>(HTTP_PATH, 'dashboard', {
        params: {
          ...params,
          GrupoSite: headerFilters?.unitGroups?.id,
          SubGrupoSite: headerFilters?.unitSubgroups?.id,
          Cidade: headerFilters?.cities?.id,
          Estado: headerFilters?.states?.id,
        },
        paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
      })
      .catch(error => {
        const message = getErrorMessageByStatusCode(error, HTTP_PATH);
        setErrorMessage(message);
        setIsError(true);
      });

    if (result) {
      const _series = result.map(({ quantidade }) => quantidade || 0);
      setSeries([{ name: 'Quantidade', data: _series }]);

      const _groupPeriods = groupBy<ChartNumberOfOccurrencesRegisteredPerWeekType>(result, d => d.periodo);
      setPeriods(_groupPeriods.map(({ name }) => name));
    }

    setLoading(false);
  };

  const handleChangeStartDate = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: Inicio } = event.target;
    setParams({ ...params, Inicio });
  };

  const handleChangeEndDate = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: Fim } = event.target;
    setParams({ ...params, Fim });
  };

  return (
    <LkpChart
      title="Quantidade de ocorrências cadastradas por semana"
      companyId={headerFilters?.companies?.id || 0}
      loading={isLoadingFinal}
      error={isError}
      errorMessage={errorMessage}
      onChartTryAgain={disableFetch ? retryFetch : fetchChartData}
      series={series}
      //width={1700}
      type="line"
      options={getLineChartOptions(periods, {
        chart: {
          height: 350,
          type: 'line',
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
      })}
      filter={<LkpChartTopFilter onChangeStartDate={handleChangeStartDate} onChangeEndDate={handleChangeEndDate} />}
    />
  );
}
